// Celebration Toasts (feat/celebrations-v1) — stack de toasts bas-droite
// affichant les paliers franchis (note Google / RPS). Fetch /api/celebrations/pending
// au mount de Dashboard.js, mark-as-seen au clic × ou sur CTA.
//
// Important : monté HORS de l'iframe du dashboard (dans le wrapper Dashboard.js),
// position: fixed + z-index: 9999 → passe au-dessus du contenu iframe.
//
// {prenom} interpolé depuis storage (Preferences natif sur mobile,
// localStorage sur web). Sanitizé : strip caractères de contrôle,
// max 30 chars. Si vide, fallback "Bravo !" sans nom.

import { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { storage } from '../utils/platform';
import { API_BASE } from '../config/api';

// Sanitize du prénom avant affichage dans le toast. Deux protections :
// - strip des caractères de contrôle (\n, \r, \t, NULL, etc.) qui peuvent
//   corrompre le rendu si la valeur en storage a été mal injectée.
// - max 30 caractères pour ne pas péter le layout du toast (largeur 380px).
function sanitizePrenom(raw) {
  if (!raw) return '';
  const cleaned = String(raw).replace(/[\x00-\x1F\x7F]/g, '').trim();
  return cleaned.slice(0, 30);
}

// Mapping event_type → config visuelle (couleurs, emoji, CTA href).
// Les wordings (title/body/cta_label) sont délégués à i18n via la fn
// `getToastWording(t, event)` ci-dessous — séparation rendu / contenu.
function getToastVisual(event) {
  const baseCtaNavigate = (jump) => `/dashboard?jump=${jump}`;
  switch (event.event_type) {
    case 'note_palier_up':
      return {
        emoji: '🎉',
        bg: '#FFF5F0',
        color: '#E8502A',
        titleColor: '#B8401F',
        ctaHref: baseCtaNavigate('graph'),
      };
    case 'note_palier_down':
      return {
        emoji: '💪',
        bg: '#F5F5F0',
        color: '#1A1A1A',
        titleColor: '#1A1A1A',
        ctaHref: baseCtaNavigate('insights'),
      };
    case 'rps_palier_up':
      return {
        emoji: '🚀',
        bg: '#FFF5F0',
        color: '#E8502A',
        titleColor: '#B8401F',
        ctaHref: null,
      };
    case 'rps_palier_down':
      return {
        emoji: '🎯',
        bg: '#F5F5F0',
        color: '#1A1A1A',
        titleColor: '#1A1A1A',
        ctaHref: baseCtaNavigate('insights'),
      };
    default:
      return null;
  }
}

// Wordings i18n par event_type. Le {prenom} interpolation custom (avec
// fallback "Bravo !" sans nom) est conservée — i18next n'a pas de
// gestion native pour "interpoler X si défini, sinon supprimer X".
function getToastWording(t, event) {
  const p = event.payload || {};
  switch (event.event_type) {
    case 'note_palier_up':
      return {
        title: t('celebration.note_palier_up_title'),
        body: t('celebration.note_palier_up_body', { seuil: p.seuil }),
        ctaLabel: t('celebration.note_palier_up_cta'),
      };
    case 'note_palier_down':
      return {
        title: t('celebration.note_palier_down_title'),
        body: t('celebration.note_palier_down_body', { seuil: p.seuil }),
        ctaLabel: t('celebration.note_palier_down_cta'),
      };
    case 'rps_palier_up':
      return {
        title: t('celebration.rps_palier_up_title', { to: p.to || '' }),
        body: t('celebration.rps_palier_up_body', { score: p.score }),
        ctaLabel: null,
      };
    case 'rps_palier_down':
      return {
        title: t('celebration.rps_palier_down_title'),
        body: t('celebration.rps_palier_down_body', { to: p.to || '' }),
        ctaLabel: t('celebration.rps_palier_down_cta'),
      };
    default:
      return null;
  }
}

// Interpolation du prénom. Si prenom vide (1er chargement post-merge ou
// storage natif pas encore hydraté), on enlève proprement le "{prenom}" et
// l'espace qui le précède pour éviter les artefacts "Bravo  !" (double espace).
// Le prenom est passé en paramètre (et non lu en interne) pour rester
// pure-fn testable et compatible avec le storage async (Capacitor Preferences).
function interpolate(str, prenom) {
  if (prenom) return String(str || '').replace('{prenom}', prenom);
  return String(str || '').replace(/\s?\{prenom\}/g, '');
}

export default function CelebrationStack() {
  const [events, setEvents] = useState([]);
  const [prenom, setPrenom] = useState('');
  const fetchedRef = useRef(false);

  // Charge le prénom depuis storage (Preferences natif sur mobile, localStorage
  // sur web) une fois au mount, avec garde anti setState-post-unmount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const raw = await storage.get('user_prenom');
      if (!cancelled) setPrenom(sanitizePrenom(raw));
    })();
    return () => { cancelled = true; };
  }, []);

  // Mark-as-seen API call (best-effort, ne bloque pas l'UX même en cas d'erreur)
  const markSeen = useCallback(async (id) => {
    try {
      const token = await storage.get('token');
      if (!token) return;
      await fetch(`${API_BASE}/api/celebrations/${id}/seen`, {
        method: 'POST',
        headers: { 'Authorization': 'Bearer ' + token }
      });
    } catch (err) {
      console.error('[CelebrationToast] markSeen error:', err.message);
    }
  }, []);

  const dismiss = useCallback((id) => {
    setEvents(prev => prev.filter(e => e.id !== id));
    markSeen(id); // fire-and-forget
  }, [markSeen]);

  // Fetch pending events au mount (1 seule fois — React strict mode safe via useRef)
  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    (async () => {
      try {
        const token = await storage.get('token');
        if (!token) return;
        const res = await fetch(`${API_BASE}/api/celebrations/pending`, {
          headers: { 'Authorization': 'Bearer ' + token }
        });
        if (!res.ok) return;
        const data = await res.json();
        if (Array.isArray(data.events) && data.events.length > 0) {
          // Max 3 affichés en simultané (la route renvoie déjà LIMIT 3 mais on double-check)
          setEvents(data.events.slice(0, 3));
        }
      } catch (err) {
        console.error('[CelebrationToast] fetch pending error:', err.message);
      }
    })();
  }, []);

  if (events.length === 0) return null;

  return (
    <>
      <style>{celebrationCss}</style>
      <div className="celebration-stack" role="status" aria-live="polite">
        {events.map((ev, idx) => (
          <CelebrationToast
            key={ev.id}
            event={ev}
            index={idx}
            prenom={prenom}
            onDismiss={() => dismiss(ev.id)}
          />
        ))}
      </div>
    </>
  );
}

function CelebrationToast({ event, index, prenom, onDismiss }) {
  const { t } = useTranslation('dashboard');
  const [leaving, setLeaving] = useState(false);
  const visual = getToastVisual(event);
  const wording = getToastWording(t, event);

  const close = useCallback(() => {
    setLeaving(true);
    // Attendre la fin de l'anim sortie avant de retirer du DOM
    setTimeout(() => onDismiss(), 300);
  }, [onDismiss]);

  // Auto-dismiss après 8s (assez pour lire + agir)
  useEffect(() => {
    const timer = setTimeout(close, 8000);
    return () => clearTimeout(timer);
  }, [close]);

  const handleCta = () => {
    // On laisse le <a href> naviguer normalement mais on marque seen avant.
    // La nav rechargera la page → le composant se remontera.
    onDismiss();
  };

  if (!visual || !wording) return null;

  // Délai échelonné pour animation en cascade si plusieurs toasts
  const animDelay = `${index * 80}ms`;

  return (
    <div
      className={`celebration-toast ${leaving ? 'leaving' : ''}`}
      style={{
        background: visual.bg,
        borderLeft: `4px solid ${visual.color}`,
        animationDelay: animDelay
      }}
    >
      <button
        className="celebration-close"
        onClick={close}
        aria-label={t('celebration.close_label')}
        type="button"
      >
        ×
      </button>
      <div className="celebration-emoji" aria-hidden="true">{visual.emoji}</div>
      <div className="celebration-body">
        <div className="celebration-title" style={{ color: visual.titleColor }}>
          {interpolate(wording.title, prenom)}
        </div>
        <div className="celebration-text">{wording.body}</div>
        {wording.ctaLabel && visual.ctaHref && (
          <a
            href={visual.ctaHref}
            onClick={handleCta}
            className="celebration-cta"
            style={{ color: visual.color, borderColor: visual.color }}
          >
            {wording.ctaLabel} →
          </a>
        )}
      </div>
    </div>
  );
}

const celebrationCss = `
.celebration-stack {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 380px;
  width: calc(100vw - 48px);
  pointer-events: none;
}
.celebration-toast {
  position: relative;
  pointer-events: auto;
  padding: 18px 22px 18px 18px;
  border-radius: 12px;
  box-shadow: 0 10px 32px rgba(0,0,0,0.18);
  display: grid;
  grid-template-columns: 36px 1fr;
  gap: 12px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Inter', sans-serif;
  animation: celebration-in 400ms cubic-bezier(0.16, 1, 0.3, 1) both;
}
.celebration-toast.leaving {
  animation: celebration-out 300ms cubic-bezier(0.4, 0, 1, 1) forwards;
}
@keyframes celebration-in {
  from { transform: translateX(110%); opacity: 0; }
  to   { transform: translateX(0);    opacity: 1; }
}
@keyframes celebration-out {
  from { transform: translateX(0);    opacity: 1; }
  to   { transform: translateX(110%); opacity: 0; }
}
.celebration-emoji {
  font-size: 26px;
  line-height: 1;
  padding-top: 2px;
}
.celebration-body { min-width: 0; }
.celebration-title {
  font-size: 15px;
  font-weight: 700;
  letter-spacing: -0.2px;
  margin-bottom: 4px;
  line-height: 1.3;
}
.celebration-text {
  font-size: 13px;
  color: #4A4A4A;
  line-height: 1.5;
}
.celebration-cta {
  display: inline-block;
  margin-top: 10px;
  padding: 7px 12px;
  font-size: 12px;
  font-weight: 600;
  border: 1.5px solid;
  border-radius: 999px;
  text-decoration: none;
  transition: background 0.15s, color 0.15s;
}
.celebration-cta:hover {
  background: currentColor;
  color: #FFFFFF !important;
}
.celebration-close {
  position: absolute;
  top: 8px;
  right: 10px;
  width: 22px;
  height: 22px;
  border: none;
  background: transparent;
  font-size: 18px;
  line-height: 1;
  color: #9A9A9A;
  cursor: pointer;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  font-family: inherit;
}
.celebration-close:hover { background: rgba(0,0,0,0.06); color: #1A1A1A; }
@media (max-width: 500px) {
  .celebration-stack { right: 16px; left: 16px; bottom: 16px; max-width: none; width: auto; }
  .celebration-toast { padding: 14px 18px 14px 16px; }
  .celebration-title { font-size: 14px; }
  .celebration-text { font-size: 12.5px; }
}
`;
